import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Card } from "../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import { taskStatusColor } from "../../../components/visuals/TaskKindIcon";
import { usePost } from "../../../hooks/usePost";
import { Task, TaskStatus } from "../../../models/workflowModels";
import { ROUTES } from "../../../routes/routeNames";
import {
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreyText,
} from "../../../styles";
import { MESSAGES } from "../../../messages";
import { ActionTrackerRow } from "./ActionTrackerRow";
import { summariseTasks, visibleTasks } from "./actionTrackerUtils";

type ActionTrackerProps = {
  height: string;
};

type SummaryItemProps = {
  value: number;
  label: string;
  dotColor?: string;
};

const TrackerCard = styled(Card)`
  flex: 1 1 0;
  container: action-tracker / inline-size;
`;

const Frame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const WorkspaceLink = styled(Link)`
  font-family: "Apercu";
  font-size: 12px;
  color: ${greyText};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    color: ${darkGreen};
  }
`;

const SummaryBar = styled.div`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  max-width: 100%;
  height: 30px;
  padding: 0 14px;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px ${lightGreyOutline};
  overflow: hidden;
`;

const SummaryItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: "Apercu";
  font-size: 12px;
  color: ${greyText};
  white-space: nowrap;
  & + & {
    margin-left: 12px;
    padding-left: 12px;
    border-left: 1px solid ${lightGreyOutline};
  }
`;

const SummaryValue = styled.span`
  font-family: "ApercuMedium";
  color: ${darkGreen};
`;

const List = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-right: -8px;
  padding-right: 8px;
`;

const Summary = ({ value, label, dotColor }: SummaryItemProps): JSX.Element => (
  <SummaryItem>
    {dotColor && <LegendCircle color={dotColor} size={7} />}
    <SummaryValue>{value}</SummaryValue>
    {label}
  </SummaryItem>
);

export const ActionTracker = ({ height }: ActionTrackerProps): JSX.Element => {
  const { data, sendData, isLoading } = usePost<Task[], null>();

  useEffect(() => {
    sendData("GET_TASKS", null);
  }, []);

  const tasks = useMemo(() => visibleTasks(data ?? []), [data]);
  const summary = useMemo(() => summariseTasks(data ?? []), [data]);

  return (
    <TrackerCard h={height} m="0 10px" isLoading={isLoading}>
      <Frame>
        <Header>
          <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
            {MESSAGES.ACTION_TRACKER.TITLE}
          </CompassParagraph>
          <WorkspaceLink to={ROUTES.WORKSPACE}>
            {MESSAGES.ACTION_TRACKER.WORKSPACE_LINK}
          </WorkspaceLink>
        </Header>
        <SummaryBar>
          <Summary value={summary.open} label={MESSAGES.ACTION_TRACKER.OPEN} />
          <Summary
            value={summary.inProgress}
            label={MESSAGES.ACTION_TRACKER.IN_PROGRESS}
            dotColor={taskStatusColor(TaskStatus.IN_PROGRESS)}
          />
          <Summary
            value={summary.done}
            label={MESSAGES.ACTION_TRACKER.DONE}
            dotColor={taskStatusColor(TaskStatus.COMPLETED)}
          />
        </SummaryBar>
        {tasks.length === 0 && !isLoading ? (
          <CompassParagraph color={lightGreyText} fontSize={13}>
            {MESSAGES.ACTION_TRACKER.EMPTY}
          </CompassParagraph>
        ) : (
          <List>
            {tasks.map((task) => (
              <ActionTrackerRow key={task.task_id} task={task} />
            ))}
          </List>
        )}
      </Frame>
    </TrackerCard>
  );
};
